import { createSelector } from 'reselect';
import queryString from 'query-string';
import get from 'lodash/get';
import values from 'lodash/values';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import camelCase from 'lodash/camelCase';
import {
  getForum,
  getQuery,
  getUi,
  getPlaces,
  getUsers,
  getParams,
  getCurrentUser,
} from './';

export const selectTopicCategories = createSelector(
  [getForum, getUi],
  ({ topicCategoryById, topicCategoriesIdsByPlaceId }, { selectedPlaceId }) => {
    const topicCategoriesIds =
      topicCategoriesIdsByPlaceId[selectedPlaceId] || [];

    return topicCategoriesIds.map(id => topicCategoryById[id]);
  },
);

export const selectCurrentCategory = createSelector(
  [selectTopicCategories, getParams],
  (categories, { categoryId }) => {
    if (categories.length === 0) {
      return null;
    }

    return find(categories, ({ id }) => id === Number(categoryId));
  },
);

export const selectTopics = createSelector(
  [
    getUi,
    getForum,
    getQuery,
    getPlaces,
    getUsers,
    selectCurrentCategory,
    getParams,
    getCurrentUser,
  ],
  (
    { selectedPlaceId, topicsSort },
    { topicById, topicsPagination, topicCategoryById },
    search,
    { equipmentById },
    { userById },
    category,
    { categoryId },
    currentUser,
  ) => {
    if (Boolean(categoryId) && !category) {
      return [];
    }

    if (search === '') {
      search = '?';
    }

    const { page, ...parsedQuery } = {
      ...queryString.parse(search),
      ...(Boolean(topicsSort) ? { sort: topicsSort } : {}),
      ...(Boolean(category)
        ? { categoryKind: camelCase(get(category, 'kind')) }
        : {}),
    };

    const query = `?${queryString.stringify({
      state: 'active',
      ...parsedQuery,
    })}`;

    const topicsIds = flatten(
      values(get(topicsPagination, `[${selectedPlaceId}][${query}].idsByPage`)),
    );

    if (!topicsIds) {
      return [];
    }

    return topicsIds.map(id => {
      const topic = topicById[id];

      return {
        ...topic,
        equipment: equipmentById[topic.equipmentId],
        category: topicCategoryById[topic.category],
        author:
          topic.author !== currentUser.id
            ? userById[topic.author]
            : { ...userById[topic.author], ...currentUser.currentIdentity },
      };
    });
  },
);

export const selectTopicsPagination = createSelector(
  [getForum, getQuery, getUi, getParams, selectCurrentCategory],
  (
    { topicsPagination },
    search,
    { selectedPlaceId, topicsSort },
    { categoryId },
    category,
  ) => {
    if (Boolean(categoryId) && !category) {
      return {};
    }

    if (!search) {
      search = '?';
    }

    const { page, ...parsedQuery } = {
      ...queryString.parse(search),
      ...(Boolean(topicsSort) ? { sort: topicsSort } : {}),
      ...(Boolean(category)
        ? { categoryKind: camelCase(get(category, 'kind')) }
        : {}),
    };

    const query = `?${queryString.stringify({
      state: 'active',
      ...parsedQuery,
    })}`;

    return get(topicsPagination, `${selectedPlaceId}[${query}]`) || {};
  },
);

export const selectTopicPostsSorting = createSelector(
  [getParams, getUi],
  (params, { postsSortByTopicId }) =>
    postsSortByTopicId[params.topicId] || '-updatedAt',
);

export const selectTopic = createSelector(
  [getForum, getUsers, getParams, getCurrentUser],
  (
    { topicById, topicCategoryById },
    { userById },
    { topicId },
    { currentUser },
  ) => {
    const topic = topicById[topicId];

    if (!topic) {
      return {
        author: {},
      };
    }

    return {
      ...topic,
      author:
        get(topic, 'author') !== get(currentUser, 'id')
          ? userById[topic.author]
          : {
              ...userById[topic.author],
              ...currentUser.currentIdentity,
            },
      category: topicCategoryById[topic.category],
    };
  },
);

export const selectTopicPostsPagination = createSelector(
  [getForum, getParams, selectTopicPostsSorting],
  ({ postsPagination }, { topicId }, sorting) => {
    const query = `?${queryString.stringify({
      sort: sorting,
    })}`;

    const topicPostsPagination = get(
      postsPagination,
      `['${topicId}']['${query}']`,
    );

    return topicPostsPagination;
  },
);

export const selectTopicPosts = createSelector(
  [getForum, selectTopicPostsPagination],
  ({ postById }, topicPostsPagination) => {
    if (!Boolean(topicPostsPagination)) {
      return [];
    }

    const postsIds = flatten(values(topicPostsPagination.idsByPage));

    const posts = postsIds.map(id => postById[id]);

    return posts;
  },
);
